/**
 * Dropdown for selecting which inventory audit run to view.
 *
 * Defaults to the most recent run. Shows last 30 runs with their timestamp
 * and status. Changing the selection updates AuditContext.currentRunId.
 */
import React from "react"
import { AuditRunSummary } from "../../api/audit"
import { useAudit } from "../../context/AuditContext"
import { THEME } from "../../config/theme"
import { parseUtcTimestamp } from "../../utils/parseUtcTimestamp"

interface Props {
  runs: AuditRunSummary[]
  loading: boolean
  error: string | null
}

function formatRunLabel(run: AuditRunSummary): string {
  // started_at is naive UTC — a bare new Date() would shift it to local time.
  const date = parseUtcTimestamp(run.started_at)
  const label = date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
  const status = run.status === "success" ? "" : ` (${run.status})`
  return `${label}${status}`
}

export default function RunSelector({ runs, loading, error }: Props) {
  const { currentRunId, setCurrentRunId } = useAudit()

  if (loading) {
    return (
      <div
        style={{
          color: THEME.colors.neutral.light.textSecondary,
          fontSize: 13,
        }}
      >
        Loading runs...
      </div>
    )
  }
  if (error) {
    return (
      <div style={{ color: THEME.colors.semantic.danger, fontSize: 13 }}>
        Failed to load runs: {error}
      </div>
    )
  }
  if (runs.length === 0) {
    return (
      <div
        style={{
          color: THEME.colors.neutral.light.textSecondary,
          fontSize: 13,
        }}
      >
        No runs yet for this group
      </div>
    )
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: THEME.spacing.sm }}>
      <label
        htmlFor="audit-run-selector"
        style={{
          fontSize: 13,
          color: THEME.colors.neutral.light.textPrimary,
          fontWeight: 500,
        }}
      >
        Run:
      </label>
      <select
        id="audit-run-selector"
        value={currentRunId ?? ""}
        onChange={(e) => setCurrentRunId(Number(e.target.value))}
        style={{
          padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
          fontSize: 13,
          borderRadius: THEME.borderRadius.md,
          border: `1px solid ${THEME.colors.silver}`,
          backgroundColor: THEME.colors.neutral.light.surface,
          color: THEME.colors.neutral.light.textPrimary,
          fontFamily: THEME.fonts.primary,
          cursor: "pointer",
          minWidth: 220,
        }}
      >
        {runs.map((run, idx) => (
          <option key={run.id} value={run.id}>
            {idx === 0 ? "Latest \u2014 " : ""}
            {formatRunLabel(run)}
          </option>
        ))}
      </select>
    </div>
  )
}
